import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useEvent } from '@/context/EventContext';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import {
  arrayMove,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import {
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { LogoUpload } from '@/components/LogoUpload';
import {
  Calendar as CalendarIcon,
  Table as TableIcon,
  Download,
  RefreshCw,
  Trophy,
  Users,
  Loader2,
  Home,
  Share2,
  BarChart3,
  Camera,
  Bell,
  ClipboardList,
  Eye,
  EyeOff,
  MapPin,
  ShieldCheck,
} from 'lucide-react';

import { ScheduleTableView } from '@/components/schedule/ScheduleTableView';
import { CalendarView } from '@/components/schedule/CalendarView';
import { StandingsView } from '@/components/schedule/StandingsView';
import { LogosView } from '@/components/schedule/LogosView';
import { ShareModal } from '@/components/schedule/ShareModal';
import { AddMatchModal } from '@/components/schedule/AddMatchModal';
import { AssignTeamModal } from '@/components/schedule/AssignTeamModal';
import { BoxScoreEntryModal } from '@/components/schedule/BoxScoreEntryModal';
import { DeleteMatchDialog } from '@/components/schedule/DeleteMatchDialog';
import { AnonSignupPromoModal } from '@/components/schedule/AnonSignupPromoModal';
import WelcomeTour from '@/components/schedule/WelcomeTour';
import { SportLeaderboard } from '@/components/schedule/SportLeaderboard';
import { PublicSeasonStats } from '@/components/schedule/PublicSeasonStats';
import { StatsSummary } from '@/components/schedule/StatsSummary';
import { CollaboratorsPanel } from '@/components/schedule/CollaboratorsPanel';
import { TeamManagementPanel } from '@/components/schedule/TeamManagementPanel';
import { FIELD_SPORTS } from '@/lib/sportStats';
import { isCricket, cricketSideError, cricketPayload } from '@/lib/cricket';
import { ManageVenuesModal } from '@/components/schedule/ManageVenuesModal';
import UserMenu from '@/components/UserMenu';
import { GoProBanner } from '@/components/GoProBanner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ScheduleView() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentEvent, fetchEvent, updateMatch, addMatch, deleteMatch, deleteMatchesBulk, regenerateSchedule, loading } = useEvent();
  const { isPro, isAuthenticated, user } = useAuth();
  // Anonymous users who just finished the guest wizard land here with
  // `?welcome=1`. Show a one-shot popup nudging them to create an
  // account or sign in — then strip the flag from the URL so a refresh
  // doesn't retrigger the modal.
  const showAnonPromo = !isAuthenticated && searchParams.get('welcome') === '1';
  const [view, setView] = useState('table');
  const [filters, setFilters] = useState({ day: 'all', court: 'all', team: 'all', stage: 'all' });
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [manageVenuesOpen, setManageVenuesOpen] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  const [copied, setCopied] = useState(false);
  const [shareLoading, setShareLoading] = useState(false);
  const [visibilityLoading, setVisibilityLoading] = useState(false);
  const [editingScore, setEditingScore] = useState(null);
  const [scoreInputs, setScoreInputs] = useState({ score_a: '', score_b: '' });
  // Volleyball-only per-set editor state. Each entry maps to one set on the
  // match doc (`{a, b}`). Empty when the event isn't volleyball.
  const [setInputs, setSetInputs] = useState([]);
  const [standings, setStandings] = useState(null);
  const [standingsLoading, setStandingsLoading] = useState(false);
  const [pointValues, setPointValues] = useState({ win: 3, loss: 0, draw: 1, forfeit: 0 });
  const [showNotifyModal, setShowNotifyModal] = useState(false);
  const [notifyMsg, setNotifyMsg] = useState('');
  const [notifySending, setNotifySending] = useState(false);
  // Which recipient groups the Notify modal should target. Collaborators is
  // on by default (matches historical behavior); Team Contacts is opt-in so
  // organizers don't accidentally spam every team when they only meant to
  // ping their co-admins.
  const [notifyToCollaborators, setNotifyToCollaborators] = useState(true);
  const [notifyToTeamContacts, setNotifyToTeamContacts] = useState(false);
  // Welcome tour. Freshly-signed-up guest users land here with
  // `?welcome_tour=1` (set by LoginPage/GoogleCallbackPage after their
  // guest event is claimed). We show the tour once per user per browser.
  const tourFlagKey = user?.user_id ? `sg_welcome_tour_seen_${user.user_id}` : 'sg_welcome_tour_seen';
  const [showWelcomeTour, setShowWelcomeTour] = useState(false);
  useEffect(() => {
    if (!isAuthenticated) return;
    const requested = searchParams.get('welcome_tour') === '1';
    if (!requested) return;
    let alreadySeen = false;
    try { alreadySeen = localStorage.getItem(tourFlagKey) === '1'; } catch { /* private mode */ }
    if (!alreadySeen) {
      // Small delay so the schedule finishes rendering before we measure
      // the highlight targets.
      const t = setTimeout(() => setShowWelcomeTour(true), 400);
      return () => clearTimeout(t);
    }
    // Either way, strip the flag so a refresh doesn't retrigger.
    const params = new URLSearchParams(searchParams);
    params.delete('welcome_tour');
    setSearchParams(params, { replace: true });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user?.user_id]);
  const [savingPoints, setSavingPoints] = useState(false);
  const [editingDateTime, setEditingDateTime] = useState(null);
  const [dateTimeInputs, setDateTimeInputs] = useState({ date: '', time: '' });
  const [dragEnabled, setDragEnabled] = useState(false);
  const [isSavingOrder, setIsSavingOrder] = useState(false);
  const [renamingTeamId, setRenamingTeamId] = useState(null);
  const [teamRenameValue, setTeamRenameValue] = useState('');
  const [showAddMatchModal, setShowAddMatchModal] = useState(false);
  const [assignTeamMatch, setAssignTeamMatch] = useState(null);
  const [boxScoreMatch, setBoxScoreMatch] = useState(null);
  const [deleteMatchTarget, setDeleteMatchTarget] = useState(null);
  const [bulkDeleteTargets, setBulkDeleteTargets] = useState(null);
  const bulkClearRef = React.useRef(null);
  const [sportDefinition, setSportDefinition] = useState(null);
  const [leaderboardRefreshKey, setLeaderboardRefreshKey] = useState(0);
  const eventSport = currentEvent?.sport || 'basketball';
  const isBasketball = eventSport === 'basketball';
  const supportsBoxScoreEntry = !isBasketball && (sportDefinition?.stats?.length > 0 || sportDefinition?.team_score_field !== undefined);

  // Load the sport definition once the event's sport is known.
  useEffect(() => {
    if (!currentEvent?.sport || currentEvent.sport === 'basketball') {
      setSportDefinition(null);
      return;
    }
    fetch(`${API_URL}/api/sports`)
      .then((r) => r.json())
      .then((d) => {
        const def = (d.sports || []).find((s) => s.key === currentEvent.sport);
        setSportDefinition(def || null);
      })
      .catch(() => setSportDefinition(null));
  }, [currentEvent?.sport]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  useEffect(() => {
    if (eventId) {
      fetchEvent(eventId).catch(() => {
        toast.error('Failed to load event');
        navigate('/');
      });
    }
  }, [eventId, fetchEvent, navigate]);

  // Guest visitors: stash the event id so a subsequent signup can attach
  // it to their new account (see AuthContext.claimPendingGuestEvent).
  // Only relevant while they're unauthenticated on a page reached via
  // ?welcome=1 (fresh guest wizard) — for authenticated users this would
  // be redundant since the event already has a `user_id`.
  useEffect(() => {
    if (!isAuthenticated && eventId && showAnonPromo) {
      try { localStorage.setItem('sg_guest_event_id', eventId); } catch { /* private mode */ }
    }
  }, [isAuthenticated, eventId, showAnonPromo]);

  useEffect(() => {
    if (currentEvent?.start_date) {
      setCalendarMonth(parseISO(currentEvent.start_date));
    }
  }, [currentEvent?.start_date]);

  const filteredSchedule = useMemo(() => {
    if (!currentEvent?.schedule) return [];
    const hasVenues = (currentEvent?.venues || []).length > 0;
    return currentEvent.schedule.filter(match => {
      if (filters.day !== 'all' && match.date !== filters.day) return false;
      // Third-column filter: for venue-based events, `filters.court` holds
      // a venue name; for court-based events, a numeric court id (as a
      // string). Same state slot, sport-appropriate semantics.
      if (filters.court !== 'all') {
        if (hasVenues) {
          if ((match.venue || '') !== filters.court) return false;
        } else if (match.court !== parseInt(filters.court)) {
          return false;
        }
      }
      if (filters.team !== 'all' && match.team_a !== filters.team && match.team_b !== filters.team) return false;
      if (filters.stage !== 'all' && !match.stage?.includes(filters.stage)) return false;
      return true;
    });
  }, [currentEvent?.schedule, currentEvent?.venues, filters]);

  const filterOptions = useMemo(() => {
    if (!currentEvent?.schedule) return { days: [], courts: [], venues: [], stages: [] };
    const days = [...new Set(currentEvent.schedule.map(m => m.date).filter(Boolean))].sort();
    const courts = [...new Set(currentEvent.schedule.map(m => m.court))].sort((a, b) => a - b);
    // Prefer the event.venues list so newly-added venues (with no matches yet)
    // show up in the filter dropdown; fall back to venues currently referenced
    // by scheduled matches.
    const scheduledVenues = new Set(currentEvent.schedule.map(m => m.venue).filter(Boolean));
    const configuredVenues = new Set((currentEvent.venues || []).filter(Boolean));
    const venues = [...new Set([...configuredVenues, ...scheduledVenues])].sort();
    const stages = [...new Set(currentEvent.schedule.map(m => {
      if (m.stage?.includes('RR')) return 'RR';
      if (m.stage?.includes('QF')) return 'QF';
      if (m.stage?.includes('SF')) return 'SF';
      if (m.stage?.includes('F')) return 'F';
      if (m.stage?.includes('LB')) return 'LB';
      return m.stage;
    }).filter(Boolean))];
    return { days, courts, venues, stages };
  }, [currentEvent?.schedule, currentEvent?.venues]);

  // --- Handlers ---

  const handleLockToggle = async (match) => {
    try {
      await updateMatch(eventId, match.id, { locked: !match.locked });
      toast.success(match.locked ? 'Match unlocked' : 'Match locked');
    } catch { toast.error('Failed to update match'); }
  };

  const handleVenueChange = async (matchId, venue) => {
    try {
      await updateMatch(eventId, matchId, { venue });
      toast.success('Venue updated');
    } catch { toast.error('Failed to update venue'); }
  };

  const handleAddVenue = async () => {
    // Inline "add venue" flow used by the table's venue Select. Prompts for a
    // name and PATCHes the event's venues list. Returns the new name so the
    // Select can auto-apply it to the current row.
    const name = (window.prompt('New venue name') || '').trim();
    if (!name) return null;
    const existing = currentEvent?.venues || [];
    if (existing.some(v => v.toLowerCase() === name.toLowerCase())) {
      toast.info('That venue already exists');
      return name;
    }
    try {
      const token = localStorage.getItem('session_token');
      const res = await fetch(`${API_URL}/api/events/${eventId}/venues`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ venues: [...existing, name] }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to add venue');
      toast.success(`Venue "${name}" added`);
      // Refresh the current event so the new venue shows in every dropdown.
      await fetchEvent(eventId);
      return name;
    } catch (e) {
      toast.error(e.message || 'Failed to add venue');
      return null;
    }
  };

  const handleRegenerate = async () => {
    if (window.confirm('This will regenerate the schedule. Locked matches will be preserved. Continue?')) {
      try {
        const result = await regenerateSchedule(eventId);
        if (result.conflicts?.length > 0) {
          toast.warning(`Schedule regenerated with ${result.conflicts.length} conflict(s)`);
        } else {
          toast.success('Schedule regenerated successfully');
        }
      } catch { toast.error('Failed to regenerate schedule'); }
    }
  };

  const handleShare = async () => {
    setShareLoading(true);
    try {
      if (currentEvent?.share_token) {
        setShareUrl(`${window.location.origin}/share/${currentEvent.share_token}`);
        setShowShareModal(true);
      } else {
        const response = await fetch(`${API_URL}/api/events/${eventId}/share`, { method: 'POST', credentials: 'include' });
        const data = await response.json();
        if (data.success) {
          setShareUrl(`${window.location.origin}${data.share_url}`);
          setShowShareModal(true);
          fetchEvent(eventId);
          toast.success('Share link created!');
        }
      }
    } catch { toast.error('Failed to create share link'); }
    finally { setShareLoading(false); }
  };

  const isOwner = useMemo(
    () => !!user?.user_id && !!currentEvent?.user_id && user.user_id === currentEvent.user_id,
    [user, currentEvent],
  );
  const canOfficiate = isOwner || (currentEvent?.collaborators || []).some(c => c?.user_id === user?.user_id && c?.role !== 'viewer');
  const officialsHref = `/officials?${isOwner ? '' : `org=${currentEvent?.user_id}&`}event=${eventId}`;

  const handleToggleVisibility = async () => {
    // Flip is_public — the single source of truth for whether any public
    // surface (share page, sport leaderboard, box-score PNG) will render.
    // When turning ON without a share_token, the backend mints one.
    if (!currentEvent) return;
    setVisibilityLoading(true);
    try {
      const nextPublic = !currentEvent.is_public;
      if (nextPublic) {
        // POST /share both creates a token if needed AND sets is_public: true.
        const token = localStorage.getItem('session_token');
        const res = await fetch(`${API_URL}/api/events/${eventId}/share`, {
          method: 'POST',
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          credentials: 'include',
        });
        if (!res.ok) throw new Error('Failed to make league visible');
        toast.success('League is now visible on public surfaces');
      } else {
        const token = localStorage.getItem('session_token');
        const res = await fetch(`${API_URL}/api/events/${eventId}/share`, {
          method: 'DELETE',
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          credentials: 'include',
        });
        if (!res.ok) throw new Error('Failed to hide league');
        toast.success('League hidden from public — schedule, leaderboards and box scores are private');
      }
      fetchEvent(eventId);
    } catch (e) {
      toast.error(e.message || 'Could not update visibility');
    } finally {
      setVisibilityLoading(false);
    }
  };

  const handleRevokeShare = async () => {
    try {
      const response = await fetch(`${API_URL}/api/events/${eventId}/share`, { method: 'DELETE', credentials: 'include' });
      const data = await response.json();
      if (data.success) {
        setShowShareModal(false);
        setShareUrl('');
        fetchEvent(eventId);
        toast.success('Share link revoked');
      }
    } catch { toast.error('Failed to revoke share link'); }
  };

  const copyToClipboard = () => {
    // Copy only the URL so pasting into chat apps produces a clean, clickable
    // link instead of "Tournament Name\nhttps://..." which breaks the link
    // in many messaging clients.
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success('Link copied to clipboard!');
  };

  const isVolleyball = (currentEvent?.sport || '').toLowerCase() === 'volleyball';
  const isCricketEvent = isCricket(currentEvent?.sport);

  const handleEditScore = (match) => {
    setEditingScore(match.id);
    if (isVolleyball) {
      // Seed the set editor with any saved sets, otherwise a single empty
      // row so the coach can start entering Set 1 immediately.
      const existing = Array.isArray(match.sets) && match.sets.length
        ? match.sets.map(s => ({
            a: s.a !== null && s.a !== undefined ? String(s.a) : '',
            b: s.b !== null && s.b !== undefined ? String(s.b) : '',
          }))
        : [{ a: '', b: '' }];
      setSetInputs(existing);
      setScoreInputs({ score_a: '', score_b: '' });
    } else {
      const str = (v) => (v !== null && v !== undefined ? String(v) : '');
      setScoreInputs({
        score_a: str(match.score_a),
        score_b: str(match.score_b),
        wickets_a: str(match.wickets_a),
        wickets_b: str(match.wickets_b),
        overs_a: str(match.overs_a),
        overs_b: str(match.overs_b),
      });
      setSetInputs([]);
    }
  };

  const handleSaveScore = async (matchId) => {
    if (isVolleyball) {
      // Validate + coerce each set's inputs. Skip fully-empty rows so an
      // organizer can add "Set 3" but leave it blank without failing save.
      const cleanSets = [];
      for (const s of setInputs) {
        const aBlank = s.a === '';
        const bBlank = s.b === '';
        if (aBlank && bBlank) continue;
        if (aBlank || bBlank) {
          toast.error('Enter both scores for every set (or remove the row)');
          return;
        }
        const a = parseInt(s.a, 10);
        const b = parseInt(s.b, 10);
        if (isNaN(a) || isNaN(b) || a < 0 || b < 0) {
          toast.error('Set scores must be non-negative numbers');
          return;
        }
        cleanSets.push({ a, b });
      }
      if (cleanSets.length === 0) {
        toast.error('Enter at least one completed set');
        return;
      }
      try {
        await updateMatch(eventId, matchId, { sets: cleanSets });
        toast.success('Set scores saved!');
        setEditingScore(null);
        setSetInputs([]);
        fetchStandings();
      } catch { toast.error('Failed to save score'); }
      return;
    }

    const score_a = scoreInputs.score_a !== '' ? parseInt(scoreInputs.score_a) : null;
    const score_b = scoreInputs.score_b !== '' ? parseInt(scoreInputs.score_b) : null;
    if (score_a === null || score_b === null) { toast.error(isCricketEvent ? 'Please enter both teams\u2019 runs' : 'Please enter both scores'); return; }
    if (![scoreInputs.score_a, scoreInputs.score_b].every((v) => /^\d+$/.test(String(v).trim()))) { toast.error('Scores must be whole numbers'); return; }
    if (score_a < 0 || score_b < 0) { toast.error('Scores cannot be negative'); return; }
    let extra = {};
    if (isCricketEvent) {
      const m = (currentEvent.schedule || []).find((x) => x.id === matchId) || {};
      const err = cricketSideError(m.team_a_name || 'Team A', scoreInputs.wickets_a, scoreInputs.overs_a)
        || cricketSideError(m.team_b_name || 'Team B', scoreInputs.wickets_b, scoreInputs.overs_b);
      if (err) { toast.error(err); return; }
      extra = cricketPayload(scoreInputs.wickets_a, scoreInputs.wickets_b, scoreInputs.overs_a, scoreInputs.overs_b);
    }
    try {
      await updateMatch(eventId, matchId, { score_a, score_b, ...extra });
      toast.success('Score saved!');
      setEditingScore(null);
      fetchStandings();
    } catch { toast.error('Failed to save score'); }
  };

  const handleCancelScore = () => {
    setEditingScore(null);
    setScoreInputs({ score_a: '', score_b: '' });
    setSetInputs([]);
  };

  const handleMarkForfeit = async (matchId, forfeitTeam) => {
    // forfeitTeam: "a" | "b" | "both" | "" (empty clears the flag)
    try {
      await updateMatch(eventId, matchId, { forfeit_team: forfeitTeam || '' });
      toast.success(forfeitTeam ? 'Match marked as forfeit' : 'Forfeit cleared');
      setEditingScore(null);
      fetchStandings();
    } catch { toast.error('Failed to update forfeit'); }
  };

  const handleEditDateTime = (match) => {
    setEditingDateTime(match.id);
    setDateTimeInputs({ date: match.date || '', time: match.time || '' });
  };

  const handleSaveDateTime = async (matchId) => {
    try {
      const updates = {};
      if (dateTimeInputs.date) updates.date = dateTimeInputs.date;
      if (dateTimeInputs.time) updates.time = dateTimeInputs.time;
      await updateMatch(eventId, matchId, updates);
      toast.success('Date/Time updated!');
      setEditingDateTime(null);
    } catch { toast.error('Failed to update date/time'); }
  };

  const handleCancelDateTime = () => { setEditingDateTime(null); setDateTimeInputs({ date: '', time: '' }); };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = currentEvent.schedule.findIndex(m => m.id === active.id);
    const newIndex = currentEvent.schedule.findIndex(m => m.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const newSchedule = arrayMove(currentEvent.schedule, oldIndex, newIndex);
    setIsSavingOrder(true);
    try {
      const token = localStorage.getItem('session_token');
      const response = await fetch(`${API_URL}/api/events/${eventId}/reorder-matches`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ match_ids: newSchedule.map(m => m.id) })
      });
      if (response.ok) { toast.success('Match order updated!'); fetchEvent(eventId); }
      else { const data = await response.json(); toast.error(data.detail || 'Failed to reorder matches'); }
    } catch { toast.error('Failed to save new order'); }
    finally { setIsSavingOrder(false); }
  };

  const fetchStandings = async () => {
    setStandingsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/events/${eventId}/standings`);
      const data = await response.json();
      setStandings(data);
      if (data.point_values) setPointValues(data.point_values);
    } catch (err) { console.error('Failed to fetch standings:', err); }
    finally { setStandingsLoading(false); }
  };

  const handleEventLogoUpload = async (storagePath) => {
    try {
      const token = localStorage.getItem('session_token');
      const response = await fetch(`${API_URL}/api/events/${eventId}/event-logo`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ logo_path: storagePath })
      });
      if (response.ok) fetchEvent(eventId);
    } catch { toast.error('Failed to save event logo'); }
  };

  const handleTeamLogoUpload = async (teamId, storagePath) => {
    try {
      const token = localStorage.getItem('session_token');
      if (storagePath) {
        await fetch(`${API_URL}/api/events/${eventId}/team-logos`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ team_logos: { [teamId]: storagePath } })
        });
      } else {
        await fetch(`${API_URL}/api/events/${eventId}/team-logos/${teamId}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        });
      }
      fetchEvent(eventId);
    } catch { toast.error('Failed to save team logo'); }
  };

  const handleTeamRename = async (teamId) => {
    const trimmed = teamRenameValue.trim();
    if (!trimmed) { toast.error('Please enter a team name'); return; }
    try {
      const token = localStorage.getItem('session_token');
      const response = await fetch(`${API_URL}/api/events/${eventId}/teams/${teamId}/name`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ name: trimmed })
      });
      const data = await response.json();
      if (response.ok) { fetchEvent(eventId); toast.success(`Team renamed to "${data.name}"`); }
      else { toast.error(data.detail || 'Failed to rename team'); }
    } catch { toast.error('Failed to rename team'); }
    finally { setRenamingTeamId(null); setTeamRenameValue(''); }
  };

  const handleSavePointValues = async () => {
    setSavingPoints(true);
    try {
      const token = localStorage.getItem('session_token');
      const response = await fetch(`${API_URL}/api/events/${eventId}/point-values`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          win_points: Number(pointValues.win),
          loss_points: Number(pointValues.loss),
          draw_points: Number(pointValues.draw),
          forfeit_points: Number(pointValues.forfeit ?? 0),
        })
      });
      const data = await response.json();
      if (response.ok) { setStandings(data); toast.success('Point values updated!'); }
      else { toast.error(data.detail || 'Failed to update point values'); }
    } catch { toast.error('Failed to save point values'); }
    finally { setSavingPoints(false); }
  };

  const getStageBadgeClass = (stage) => {
    if (!stage) return 'bg-slate-100 text-slate-600';
    if (stage.includes('RR')) return 'stage-rr';
    if (stage.includes('F') && !stage.includes('SF') && !stage.includes('QF')) return 'stage-f';
    if (stage.includes('SF') || stage.includes('QF')) return 'stage-sf';
    if (stage.includes('LB')) return 'bg-purple-100 text-purple-700';
    return 'bg-slate-100 text-slate-600';
  };

  useEffect(() => { if (eventId) fetchStandings(); }, [eventId]);
  useEffect(() => { if (view === 'standings' && eventId) fetchStandings(); }, [view]);

  // --- Loading / Not Found ---

  if (loading && !currentEvent) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#00247D]" />
      </div>
    );
  }

  if (!currentEvent) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center">
        <p className="text-slate-600 mb-4">Event not found</p>
        <Button onClick={() => navigate('/')}>Go Home</Button>
      </div>
    );
  }

  // --- Render ---

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="glass-effect sticky top-0 z-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2 sm:gap-4 min-w-0">
              <Button variant="ghost" data-testid="back-home-btn" onClick={() => navigate('/')} className="text-slate-600 px-2 sm:px-4">
                <Home className="w-5 h-5" />
              </Button>
              {isPro && (
                <LogoUpload type="event" currentLogo={currentEvent.event_logo} onUploadComplete={handleEventLogoUpload} size="sm" />
              )}
              {!isPro && currentEvent.event_logo && (
                <img src={`${API_URL}/api/files/${currentEvent.event_logo}`} alt="Event logo" className="w-10 h-10 rounded-lg object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
              )}
              <div className="min-w-0">
                <h1 className="text-xl font-bold text-[#00247D] truncate max-w-[92px] sm:max-w-md" style={{ fontFamily: 'Barlow Condensed' }} title={currentEvent.name}>
                  {currentEvent.name}
                </h1>
                <div className="hidden sm:flex items-center gap-3 text-xs text-slate-500 whitespace-nowrap overflow-hidden">
                  <span className="flex items-center gap-1"><CalendarIcon className="w-3 h-3" />{currentEvent.start_date}</span>
                  <span className="flex items-center gap-1"><Users className="w-3 h-3" />{currentEvent.teams?.length} teams</span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
              <Button variant="outline" data-testid="view-dashboard-btn" onClick={() => navigate('/my-schedules')} className="hidden sm:flex">
                <BarChart3 className="w-4 h-4 mr-2" />Dashboard
              </Button>
              {canOfficiate && (
                <Button variant="outline" data-testid="officials-btn" onClick={() => navigate(officialsHref)} title="Assign officials to this schedule's games"
                  className="px-2.5 sm:px-4 border-[#00247D]/30 text-[#00247D] hover:bg-[#00247D]/5">
                  <ShieldCheck className="w-4 h-4 sm:mr-2" /><span className="hidden sm:inline">Officials</span>
                </Button>
              )}
              {['single_elimination', 'double_elimination', 'hybrid', 'consolation'].includes(currentEvent.format) && (
                <Button variant="outline" data-testid="view-bracket-btn" onClick={() => navigate(`/bracket/${eventId}`)} className="hidden sm:flex">
                  <Trophy className="w-4 h-4 mr-2" />Bracket
                </Button>
              )}
              <Button variant="outline" data-testid="share-btn" onClick={handleShare} disabled={shareLoading} className="hidden sm:flex">
                {shareLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Share2 className="w-4 h-4 mr-2" />}Share
              </Button>
              {isOwner && (
                <Button
                  variant="outline"
                  data-testid="visibility-toggle-btn"
                  onClick={handleToggleVisibility}
                  disabled={visibilityLoading}
                  title={currentEvent.is_public
                    ? 'This league is visible on the public share URL, leaderboards, and box scores. Click to hide.'
                    : 'This league is hidden from all public surfaces. Click to make visible.'}
                  className={`hidden sm:flex ${
                    currentEvent.is_public
                      ? 'border-emerald-300 text-emerald-700 hover:bg-emerald-50'
                      : 'border-slate-300 text-slate-500 hover:bg-slate-50'
                  }`}
                >
                  {visibilityLoading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : currentEvent.is_public ? (
                    <Eye className="w-4 h-4 mr-2" />
                  ) : (
                    <EyeOff className="w-4 h-4 mr-2" />
                  )}
                  {currentEvent.is_public ? 'Visible' : 'Hidden'}
                </Button>
              )}
              {isPro && (
                <Button variant="outline" data-testid="notify-btn" onClick={() => setShowNotifyModal(true)} className="hidden sm:flex">
                  <Bell className="w-4 h-4 mr-2" />Notify
                </Button>
              )}
              <Button data-testid="export-btn" onClick={() => navigate(`/export/${eventId}`)} className="bg-[#00247D] hover:bg-[#001A5C]">
                <Download className="w-4 h-4 sm:mr-2" /><span className="hidden sm:inline">Export</span>
              </Button>
              <UserMenu inline={true} />
            </div>
          </div>
        </div>
      </header>
      <GoProBanner />

      {/* Toolbar */}
      <div className="bg-white border-b border-slate-200 py-3">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <Tabs value={view} onValueChange={setView} className="w-auto">
              <TabsList className="bg-slate-100">
                <TabsTrigger value="table" data-testid="view-table-btn" className="flex items-center gap-2">
                  <TableIcon className="w-4 h-4" /><span className="hidden sm:inline">Table</span>
                </TabsTrigger>
                <TabsTrigger value="calendar" data-testid="view-calendar-btn" className="flex items-center gap-2">
                  <CalendarIcon className="w-4 h-4" /><span className="hidden sm:inline">Calendar</span>
                </TabsTrigger>
                <TabsTrigger value="standings" data-testid="view-standings-btn" className="flex items-center gap-2">
                  <BarChart3 className="w-4 h-4" /><span className="hidden sm:inline">Standings</span>
                </TabsTrigger>
                {isPro && (
                  <TabsTrigger value="logos" data-testid="view-logos-btn" className="flex items-center gap-2">
                    <Camera className="w-4 h-4" /><span className="hidden sm:inline">Logos / Teams</span>
                  </TabsTrigger>
                )}
                {!isBasketball && (
                  <TabsTrigger value="leaderboard" data-testid="view-leaderboard-btn" className="flex items-center gap-2">
                    <Trophy className="w-4 h-4" /><span className="hidden sm:inline">Leaderboard</span>
                  </TabsTrigger>
                )}
                {!isBasketball && (
                  <TabsTrigger value="season-stats" data-testid="view-season-stats-btn" className="flex items-center gap-2">
                    <BarChart3 className="w-4 h-4" /><span className="hidden sm:inline">Season Stats</span>
                  </TabsTrigger>
                )}
                {isPro && isBasketball && (
                  <button
                    type="button"
                    onClick={() => navigate(`/stats?event_id=${eventId}`)}
                    className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-slate-600 rounded-md hover:bg-white hover:text-[#00247D] transition-colors whitespace-nowrap"
                    title="Open Stats for this event"
                    data-testid="view-stats-btn">
                    <ClipboardList className="w-4 h-4" /><span className="hidden sm:inline">Stats</span>
                  </button>
                )}
              </TabsList>
            </Tabs>

            <div className="flex flex-wrap items-center gap-2">
              <Select value={filters.day} onValueChange={(v) => setFilters(f => ({ ...f, day: v }))}>
                <SelectTrigger data-testid="filter-day" className="w-[130px] h-9"><SelectValue placeholder="Day" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Days</SelectItem>
                  {filterOptions.days.map(day => (
                    <SelectItem key={day} value={day}>{format(parseISO(day), 'MMM d')}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={filters.court} onValueChange={(v) => setFilters(f => ({ ...f, court: v }))}>
                <SelectTrigger data-testid="filter-court" className="w-[140px] h-9">
                  <SelectValue placeholder={(currentEvent.venues?.length || 0) > 0 ? 'Venue' : 'Court'} />
                </SelectTrigger>
                <SelectContent>
                  {(currentEvent.venues?.length || 0) > 0 ? (
                    <>
                      <SelectItem value="all">All Venues</SelectItem>
                      {filterOptions.venues.map(v => (
                        <SelectItem key={v} value={v}>{v}</SelectItem>
                      ))}
                    </>
                  ) : (
                    <>
                      <SelectItem value="all">All {FIELD_SPORTS.includes(eventSport) ? 'Fields' : 'Courts'}</SelectItem>
                      {filterOptions.courts.map(court => (
                        <SelectItem key={court} value={court.toString()}>{FIELD_SPORTS.includes(eventSport) ? 'Field' : 'Court'} {court}</SelectItem>
                      ))}
                    </>
                  )}
                </SelectContent>
              </Select>

              <Select value={filters.team} onValueChange={(v) => setFilters(f => ({ ...f, team: v }))}>
                <SelectTrigger data-testid="filter-team" className="w-[140px] h-9"><SelectValue placeholder="Team" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Teams</SelectItem>
                  {currentEvent.teams?.map(team => (
                    <SelectItem key={team.id} value={team.id}>{team.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Button variant="outline" size="sm" data-testid="regenerate-btn" onClick={handleRegenerate} disabled={loading} className="h-9">
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />Regenerate
              </Button>
              {/* Always-visible venues entry point. When the event has no
                  venues configured the schedule's Court column defaults to
                  numeric courts and there's otherwise no way to add one, so
                  this button doubles as the "add first venue" discovery
                  path. Label flips based on the current list. */}
              <Button
                variant="outline"
                size="sm"
                className="h-9"
                onClick={() => setManageVenuesOpen(true)}
                data-testid="manage-venues-btn"
                title={(currentEvent.venues?.length || 0) > 0 ? 'Rename or remove venues' : 'Add a venue so every match gets one'}
              >
                <MapPin className="w-4 h-4 mr-2" />
                {(currentEvent.venues?.length || 0) > 0 ? 'Venues' : 'Add Venue'}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <ManageVenuesModal
        open={manageVenuesOpen}
        onClose={() => setManageVenuesOpen(false)}
        event={currentEvent}
        onChanged={() => fetchEvent(eventId)}
      />

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {view === 'table' && (
          <ScheduleTableView
            currentEvent={currentEvent}
            filteredSchedule={filteredSchedule}
            isPro={isPro}
            canScore={isAuthenticated}
            dragEnabled={dragEnabled}
            setDragEnabled={setDragEnabled}
            isSavingOrder={isSavingOrder}
            sensors={sensors}
            handleDragEnd={handleDragEnd}
            editingDateTime={editingDateTime}
            dateTimeInputs={dateTimeInputs}
            setDateTimeInputs={setDateTimeInputs}
            handleEditDateTime={handleEditDateTime}
            handleSaveDateTime={handleSaveDateTime}
            handleCancelDateTime={handleCancelDateTime}
            handleVenueChange={handleVenueChange}
            handleAddVenue={handleAddVenue}
            editingScore={editingScore}
            scoreInputs={scoreInputs}
            setScoreInputs={setScoreInputs}
            isVolleyball={isVolleyball}
            isCricket={isCricketEvent}
            setInputs={setInputs}
            setSetInputs={setSetInputs}
            handleEditScore={handleEditScore}
            handleSaveScore={handleSaveScore}
            handleCancelScore={handleCancelScore}
            handleMarkForfeit={handleMarkForfeit}
            handleLockToggle={handleLockToggle}
            getStageBadgeClass={getStageBadgeClass}
            onAssignTeams={(m) => setAssignTeamMatch(m)}
            onAddMatch={() => setShowAddMatchModal(true)}
            onEnterBoxScore={supportsBoxScoreEntry && isPro ? (m) => setBoxScoreMatch(m) : undefined}
            onDeleteMatch={(match) => setDeleteMatchTarget(match)}
            onBulkDeleteMatches={(matches, clearFn) => {
              bulkClearRef.current = clearFn;
              setBulkDeleteTargets(matches);
            }}
          />
        )}

        {view === 'calendar' && (
          <CalendarView
            currentEvent={currentEvent}
            calendarMonth={calendarMonth}
            setCalendarMonth={setCalendarMonth}
          />
        )}

        {view === 'standings' && (
          <StandingsView
            isPro={isPro}
            standings={standings}
            standingsLoading={standingsLoading}
            pointValues={pointValues}
            setPointValues={setPointValues}
            savingPoints={savingPoints}
            handleSavePointValues={handleSavePointValues}
          />
        )}

        {view === 'logos' && isPro && (
          <LogosView
            currentEvent={currentEvent}
            handleEventLogoUpload={handleEventLogoUpload}
            handleTeamLogoUpload={handleTeamLogoUpload}
            renamingTeamId={renamingTeamId}
            setRenamingTeamId={setRenamingTeamId}
            teamRenameValue={teamRenameValue}
            setTeamRenameValue={setTeamRenameValue}
            handleTeamRename={handleTeamRename}
          />
        )}

        {view === 'leaderboard' && !isBasketball && (
          <SportLeaderboard eventId={eventId} refreshKey={leaderboardRefreshKey} />
        )}

        {view === 'season-stats' && !isBasketball && currentEvent && (
          <div className="mt-6" data-testid="owner-season-stats-view">
            <PublicSeasonStats
              shareToken={currentEvent.share_token || ''}
              eventId={eventId}
              leaderboardShareToken={currentEvent.leaderboard_share_token || ''}
              eventTeams={currentEvent.teams || []}
              sport={currentEvent.sport || 'basketball'}
            />
          </div>
        )}

        {view === 'logos' && isAuthenticated && (
          <div className="mt-6">
            <CollaboratorsPanel eventId={eventId} isOwner={true} isPro={isPro} />
          </div>
        )}

        {view === 'logos' && isPro && currentEvent && (
          <TeamManagementPanel
            eventId={eventId}
            teams={currentEvent.teams || []}
            isPro={isPro}
            initialLockAfterGames={currentEvent.roster_lock_after_games || 0}
            eventName={currentEvent.name || ''}
            shareToken={currentEvent.share_token || ''}
          />
        )}

        <StatsSummary currentEvent={currentEvent} filterOptions={filterOptions} />
      </main>

      {showShareModal && (
        <ShareModal
          shareUrl={shareUrl}
          copied={copied}
          copyToClipboard={copyToClipboard}
          handleRevokeShare={handleRevokeShare}
          onClose={() => setShowShareModal(false)}
          eventName={currentEvent?.name}
          eventId={eventId}
          shareTheme={currentEvent?.share_theme}
          onThemeSaved={() => fetchEvent(eventId)}
        />
      )}

      {showAddMatchModal && (
        <AddMatchModal
          open={showAddMatchModal}
          onClose={() => setShowAddMatchModal(false)}
          event={currentEvent}
          addMatch={addMatch}
        />
      )}

      {boxScoreMatch && sportDefinition && (
        <BoxScoreEntryModal
          open={!!boxScoreMatch}
          onClose={() => setBoxScoreMatch(null)}
          event={currentEvent}
          match={boxScoreMatch}
          sportDefinition={sportDefinition}
          onSaved={() => {
            // Refresh the event so the mirrored score_a/score_b shows up
            // in the schedule table, and bump the leaderboard cache.
            fetchEvent(eventId);
            setLeaderboardRefreshKey((k) => k + 1);
          }}
        />
      )}

      {assignTeamMatch && (
        <AssignTeamModal
          open={!!assignTeamMatch}
          onClose={() => setAssignTeamMatch(null)}
          event={currentEvent}
          match={assignTeamMatch}
          updateMatch={updateMatch}
        />
      )}

      <DeleteMatchDialog
        open={!!deleteMatchTarget}
        match={deleteMatchTarget}
        onCancel={() => setDeleteMatchTarget(null)}
        onConfirm={async (match) => {
          try {
            await deleteMatch(currentEvent.id, match.id);
            toast.success('Match deleted');
            setDeleteMatchTarget(null);
          } catch (e) {
            toast.error(e?.message || 'Failed to delete match');
          }
        }}
      />

      <DeleteMatchDialog
        open={!!bulkDeleteTargets}
        matches={bulkDeleteTargets || []}
        onCancel={() => setBulkDeleteTargets(null)}
        onConfirm={async (matches) => {
          try {
            const res = await deleteMatchesBulk(
              currentEvent.id,
              matches.map((m) => m.id),
            );
            const n = res?.deleted_count ?? matches.length;
            toast.success(`${n} match${n === 1 ? '' : 'es'} deleted`);
            if (bulkClearRef.current) {
              bulkClearRef.current();
              bulkClearRef.current = null;
            }
            setBulkDeleteTargets(null);
          } catch (e) {
            toast.error(e?.message || 'Failed to delete matches');
          }
        }}
      />

      {showAnonPromo && (
        <AnonSignupPromoModal
          eventId={eventId}
          onDismiss={() => {
            // Drop the ?welcome=1 flag so a refresh doesn't retrigger.
            const next = new URLSearchParams(searchParams);
            next.delete('welcome');
            setSearchParams(next, { replace: true });
          }}
        />
      )}

      {showNotifyModal && (() => {
        const collabCount = (currentEvent?.collaborators || []).filter(c => c?.email && c?.status !== 'removed').length;
        const teamContactEmails = new Set();
        (currentEvent?.teams || []).forEach(t => {
          const em = (t?.contact?.email || '').trim().toLowerCase();
          if (em) teamContactEmails.add(em);
        });
        const teamContactCount = teamContactEmails.size;
        const parts = [];
        if (notifyToCollaborators) parts.push(`${collabCount} collaborator${collabCount === 1 ? '' : 's'}`);
        if (notifyToTeamContacts) parts.push(`${teamContactCount} team contact${teamContactCount === 1 ? '' : 's'}`);
        const totalSelected = (notifyToCollaborators ? collabCount : 0) + (notifyToTeamContacts ? teamContactCount : 0);
        const noGroupSelected = !notifyToCollaborators && !notifyToTeamContacts;
        return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowNotifyModal(false)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6" onClick={e => e.stopPropagation()} data-testid="notify-modal">
            <h3 className="text-lg font-bold text-[#00247D] mb-4" style={{ fontFamily: 'Barlow Condensed' }}>SEND NOTIFICATION</h3>
            <textarea
              className="w-full h-24 p-3 text-sm border border-slate-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-[#00247D]/20"
              placeholder="e.g. Schedule times updated for Round 2..."
              value={notifyMsg}
              onChange={e => setNotifyMsg(e.target.value)}
              data-testid="notify-message"
            />
            <div className="mt-4 space-y-2">
              <p className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Send to</p>
              <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                <input
                  type="checkbox"
                  className="w-4 h-4 rounded border-slate-300 text-[#00247D] focus:ring-[#00247D]"
                  checked={notifyToCollaborators}
                  onChange={e => setNotifyToCollaborators(e.target.checked)}
                  data-testid="notify-checkbox-collaborators"
                />
                <span>Collaborators <span className="text-slate-400">({collabCount})</span></span>
              </label>
              <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                <input
                  type="checkbox"
                  className="w-4 h-4 rounded border-slate-300 text-[#00247D] focus:ring-[#00247D]"
                  checked={notifyToTeamContacts}
                  onChange={e => setNotifyToTeamContacts(e.target.checked)}
                  data-testid="notify-checkbox-team-contacts"
                />
                <span>Team Contacts <span className="text-slate-400">({teamContactCount})</span></span>
              </label>
              {!noGroupSelected && (
                <p className="text-xs text-slate-500" data-testid="notify-recipient-summary">
                  Will notify {parts.join(' + ')} ({totalSelected} email{totalSelected === 1 ? '' : 's'}).
                </p>
              )}
              {noGroupSelected && (
                <p className="text-xs text-red-500" data-testid="notify-recipient-warning">
                  Pick at least one recipient group.
                </p>
              )}
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => setShowNotifyModal(false)}>Cancel</Button>
              <Button className="bg-[#00247D] hover:bg-[#001A5C]" disabled={notifySending || !notifyMsg.trim() || noGroupSelected || totalSelected === 0} data-testid="send-notify-btn"
                onClick={async () => {
                  setNotifySending(true);
                  try {
                    const token = localStorage.getItem('session_token');
                    const recipients = [];
                    if (notifyToCollaborators) recipients.push('collaborators');
                    if (notifyToTeamContacts) recipients.push('team_contacts');
                    const res = await fetch(`${API_URL}/api/events/${eventId}/notify`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                      body: JSON.stringify({ message: notifyMsg, type: 'update', recipients })
                    });
                    const data = await res.json();
                    if (res.ok) {
                      toast.success(data.message || 'Notification sent');
                      setShowNotifyModal(false);
                      setNotifyMsg('');
                    } else {
                      toast.error(data.detail || 'Failed to send');
                    }
                  } catch { toast.error('Failed to send notification'); }
                  finally { setNotifySending(false); }
                }}>
                {notifySending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Bell className="w-4 h-4 mr-2" />}
                Send Notification
              </Button>
            </div>
          </div>
        </div>
        );
      })()}

      <WelcomeTour
        open={showWelcomeTour}
        storageKey={tourFlagKey}
        onClose={() => {
          setShowWelcomeTour(false);
          // Strip the URL flag so a manual refresh doesn't re-open it.
          const params = new URLSearchParams(searchParams);
          if (params.get('welcome_tour')) {
            params.delete('welcome_tour');
            setSearchParams(params, { replace: true });
          }
        }}
        steps={[
          {
            title: 'Welcome to Schedule Guru',
            body: "Your schedule is now saved to your account. Let's take a quick tour of what you can do next.",
          },
          {
            title: 'Update scores here',
            body: 'Click any score cell in the table to enter results. Standings will refresh instantly as you record wins and losses.',
            selector: '[data-testid="view-table-btn"]',
          },
          {
            title: 'Upload team logos',
            body: 'Drop in team logos so they show up on the schedule, standings, and shareable graphics.',
            selector: '[data-testid="view-logos-btn"]',
          },
          {
            title: 'Live standings',
            body: 'Watch standings update in real time as you enter scores — no manual sorting needed.',
            selector: '[data-testid="view-standings-btn"]',
          },
          {
            title: 'Share your schedule',
            body: 'Generate a read-only public link so teams, parents, and fans can view it without an account.',
            selector: '[data-testid="share-btn"]',
          },
          {
            title: 'Export & print',
            body: 'Download the schedule as PDF, Excel, or iCal — perfect for handouts and calendar sync.',
            selector: '[data-testid="export-btn"]',
          },
        ]}
      />
    </div>
  );
}
